import { useState, useEffect, useRef } from 'react';
import { 
  personSetDetailsForCpp,
  personDisplayInfoForCpp
} from './structureFunctions';
import defaultExplanations from './explanations';
import codeSamplesTemplate from './codeSamples';
import { useSpeechSynthesis } from '../speechUtils';

export const useStructureType = (speakingLang, translatedExplanations, language, initialStructure, breakpoints) => {
  const [explanations, setExplanations] = useState(defaultExplanations);
  const [highlightedLine, setHighlightedLine] = useState(null);
  const [callingLine, setCallingLine] = useState(null);
  const [focusedEndLine, setFocusedEndLine] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(isPaused);
  const [delay, setDelay] = useState(2000);
  const breakpointsRef = useRef(breakpoints);
  const [logs, setLogs] = useState([]);
  const [variables, setVariables] = useState({});
  const [structureVariables, setStructureVariables] = useState({});
  const [highlightedVariables, setHighlightedVariables] = useState([]);
  const [stringVariables, setStringVariables] = useState({});
  const [highlightedStringVariables, setHighlightedStringVariables] = useState([]);
  const [highlightedStructureVariables, setHighlightedStructureVariables] = useState([]);
  const [highlightedStructureMethods, setHighlightedStructureMethods] = useState([]);
  const [codeSamples, setCodeSamples] = useState(codeSamplesTemplate[language]);
  const { speak, setIsMuted } = useSpeechSynthesis(isPausedRef, speakingLang);
  const [isMuted, setLocalIsMuted] = useState(false);
  const delayRef = useRef(delay);
  const isMutedRef = useRef(isMuted);


  useEffect(() => {
    setIsMuted(isMuted);
  }, [isMuted]);

  useEffect(() => {
    breakpointsRef.current = breakpoints;
  }, [breakpoints]);

  useEffect(() => {
    if (translatedExplanations && language) {
      setExplanations((prevExplanations) => ({
        ...prevExplanations,
        [language]: Object.keys(prevExplanations[language] || {}).reduce((acc, key, index) => {
          acc[key] = translatedExplanations[index] || prevExplanations[language][key];
          return acc;
        }, {}),
      }));
    } else {
      setExplanations(defaultExplanations);
    }
  }, [translatedExplanations, language]);

  const updateVariables = (newInitialStructure) => {
    resetState();

     // Dynamically update the code samples
     let updatedCodeSamples = "";
     if (language === 'C++') {
      updatedCodeSamples = codeSamplesTemplate[language].map((line) =>
        line
          .replace(
            /setDetails\(\s*["'][^"']*["']\s*,\s*\d+\s*\)/,
            `setDetails("${newInitialStructure?.name}", ${newInitialStructure?.age})`
          )
      );
     } else {
      updatedCodeSamples = codeSamplesTemplate[language].map((line) =>
        line
          .replace(
            /snprintf\(\s*([a-zA-Z0-9_.]+)\s*,\s*sizeof\(\1\)\s*,\s*["'][^"']*["']\s*\);/,
            `snprintf($1, sizeof($1), "${newInitialStructure?.name}");`
          )
          .replace(
            /student1\.age\s*=\s*\d+\s*;/,
            `student1.age = ${newInitialStructure?.age};`
          )
      );
     }
     
    setCodeSamples(updatedCodeSamples);
  };

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    delayRef.current = delay;
  }, [delay]);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const resetState = () => {
    setHighlightedLine(null);
    setCallingLine(null);
    setFocusedEndLine(null);
    setVariables({});
    setStructureVariables({});
    setLogs([]);
    setHighlightedVariables([]);
    setHighlightedStructureVariables([]);
    setHighlightedStructureMethods([]);
    setStringVariables({});
    setHighlightedStringVariables([]);
  };

  const logMessage = async (message) => {
    setLogs((prevLogs) => [...prevLogs, message]);
    await speak(message);
  };

  const highlightLine = async (lineNumber, customLog=null) => {
    setCallingLine((prevCallingLine) => {
      if (prevCallingLine === lineNumber) {
        return null;
      }
      return prevCallingLine;
    });
    setHighlightedLine(lineNumber);    
    
    const logMessageText = customLog 
    ? `Line ${lineNumber + 1}: ${customLog}` 
    : `Line ${lineNumber + 1}: ${explanations[language][lineNumber + 1] || "Executing line " + (lineNumber + 1)}`;

    await logMessage(logMessageText);
  };

  const highlightCallingLine = async (lineNumber) => {
    setCallingLine(lineNumber);
  };

  const focusEndLine = async (lineNumber) => {
    setFocusedEndLine(lineNumber);
    let message = "End of the execution";
    await speak(message);
  };

  const customSleep = async (lineNumber = null, time=null) => {
    // Check if the current line matches a breakpoint
    if (lineNumber !== null && breakpointsRef.current.includes(lineNumber)) {
      setIsPaused(true); // Pause execution
      isPausedRef.current = true;

      // Wait until `isPaused` is set to false
      await new Promise((resolve) => {
          const interval = setInterval(() => {
              if (!isPausedRef.current) {
                  clearInterval(interval);
                  resolve();
              }
          }, 100);
      });

      return;
    }

    // Handle regular sleep with pause checks
    let start = Date.now();
    let ms;
    if (time === null) {
        ms = delayRef.current;
    } else {
        ms = time;
    }
    
    while (Date.now() - start < ms) {
      if (isPausedRef.current) {
        await new Promise((resolve) => {
          let interval = setInterval(() => {
            if (!isPausedRef.current) {
              clearInterval(interval);
              resolve();
            }
          }, 100);
        });
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  };

  const runStructureTypeC = async () => {
    resetState();
    setIsRunning(true);
    setIsPaused(false);

    await highlightLine(8);
    let structureAttributes = [
      {
        name: 'name',
        dataType: 'string'
      },
      {
        name: 'age',
        dataType: 'int'
      }
    ];

    setStructureVariables((vars) => ({ ...vars, student1: { 
      objectName: 'student1', 
      structureName: 'Student',
      value: {attributes: structureAttributes, methods: []} 
    } }));
    setHighlightedStructureVariables(['student1']);
    await customSleep(8);
    setHighlightedStructureVariables([]);

    await highlightLine(10);
    setStringVariables((vars) => ({ ...vars, name: { variable_name: 'name', value: initialStructure.name } }));
    setHighlightedStringVariables(['name']);
    await customSleep(10);
    setHighlightedStringVariables([]);

    await highlightLine(11);
    setVariables((vars) => ({ ...vars, age: { variable_name: 'age', value: initialStructure.age } }));
    setHighlightedVariables(['age']);
    await customSleep(11);
    setHighlightedVariables([]);

    await highlightLine(13);
    setHighlightedStringVariables(['name']);
    await logMessage(`Student Name: ${initialStructure.name}`);
    await customSleep(13);
    setHighlightedStringVariables([]);

    await highlightLine(14);
    setHighlightedVariables(['age']);
    await logMessage(`Student Age: ${initialStructure.age}`);
    await customSleep(14);
    setHighlightedVariables([]);

    await highlightLine(16);
    await customSleep(16)

    await focusEndLine(17);
    setIsRunning(false);
  };

  const runStructureTypeCpp = async () => {
    resetState();
    setIsRunning(true);
    setIsPaused(false);

    await highlightLine(19);
    let structureAttributes = [
      {
        name: 'name',
        dataType: 'string'
      },
      {
        name: 'age',
        dataType: 'int'
      }
    ];

    let structureMethods = [
      {
        name: 'setDetails',
        type: 'member',
        parameters: [
          {name: 'personName', dataType: 'string'},
          {name: 'personAge', dataType: 'int'}
        ],
        returnType: 'void'
      },
      {
        name: 'displayInfo',
        type: 'member',
        parameters: [],
        returnType: 'void'
      }
    ];

    setStructureVariables((vars) => ({ ...vars, person1: { 
      objectName: 'person1', 
      structureName: 'Person',
      value: {attributes: structureAttributes, methods: structureMethods} 
    } }));
    setHighlightedStructureVariables(['person1']);
    await customSleep(19);
    setHighlightedStructureVariables([]);

    await highlightLine(20);
    await customSleep(20);

    await highlightCallingLine(20);
    setHighlightedStructureMethods(['setDetails']);
    await personSetDetailsForCpp(
      initialStructure,
      highlightLine,
      setStringVariables,
      setHighlightedStringVariables,
      setVariables,
      setHighlightedVariables,
      customSleep
    )
    setHighlightedStructureMethods([]);
    await highlightLine(20, "Returning from the setDetails method");
    await customSleep(20);

    await highlightLine(21);
    await customSleep(21);

    await highlightCallingLine(21);
    setHighlightedStructureMethods(['displayInfo']);
    await personDisplayInfoForCpp(
      initialStructure,
      highlightLine,
      setStringVariables,
      setHighlightedStringVariables,
      setVariables,
      setHighlightedVariables,
      customSleep,
      logMessage
    )
    setHighlightedStructureMethods([]);
    await highlightLine(21, "Returning from the displayInfo method");
    await customSleep(21);

    await highlightLine(23);
    await customSleep(23)

    await focusEndLine(24);
    setIsRunning(false);
  };

  const runStructureType = async () => {
    if (language === 'C') {
      await runStructureTypeC();
    } else if (language === 'C++') {
      await runStructureTypeCpp();
    }
  };

  return {
    highlightedLine,
    callingLine,
    focusedEndLine,
    isRunning,
    isPaused,
    delay,
    logs,
    variables,
    structureVariables,
    stringVariables,
    highlightedVariables,
    highlightedStructureVariables,
    highlightedStructureMethods,
    highlightedStringVariables,
    isMuted,
    codeSamples,
    speak,
    setIsMuted: setLocalIsMuted,
    updateVariables,
    setHighlightedLine,
    setCallingLine,
    setFocusedEndLine,
    setIsRunning,
    setIsPaused,
    setDelay,
    setLogs,
    setVariables,
    setStructureVariables,
    setStringVariables,
    setHighlightedVariables,
    setHighlightedStructureVariables,
    setHighlightedStructureMethods,
    setHighlightedStringVariables,
    runStructureType
  };
};
